import { Injectable, signal } from '@angular/core';
import { CurrentUserGQL, CurrentUserQuery } from '@/common/graphql/generated';
import { normalizeApolloError } from '@/common/services/error/error.mapper';
import { catchError, map, of, tap } from 'rxjs';

export type AuthStatus = 'unknown' | 'authenticated' | 'unauthenticated';

@Injectable({ providedIn: 'root' })
export class AuthStateService {
  private readonly _status = signal<AuthStatus>('unknown');
  private readonly _user = signal<CurrentUserQuery['me'] | null>(null);
  private lastChecked = 0;
  private readonly ttlMs = 5 * 60 * 1000;
  /**
   * Orders session-state writes and refresh starts so a stale response can
   * never roll back a newer operation when refreshes overlap.
   */
  private writeSeq = 0;
  private appliedWriteSeq = 0;
  /**
   * Highest sequence of an actual state write (applied response, login,
   * logout, explicit rejection). Refresh *starts* do not advance it, so a
   * newer refresh merely starting cannot discard an explicit server
   * rejection from an older in-flight request.
   */
  private lastStateWriteSeq = 0;
  /**
   * Monotonic token that advances only when the underlying session is
   * replaced or cleared (login/account switch, logout, expiry, explicit
   * rejection) - never on an ordinary user-data refresh that keeps the same
   * session. Request handlers capture it at request start so a routine
   * refresh cannot invalidate the stale-session check of an in-flight
   * request, while a newer login/logout still discards older logout
   * decisions. Kept separate from {@link lastStateWriteSeq} because that
   * counter must advance on every applied write for stale-write protection.
   */
  private sessionGenerationSeq = 0;

  status = this._status.asReadonly();
  user = this._user.asReadonly();

  constructor(private readonly meGql: CurrentUserGQL) {}

  /** Call once at app start or first need. Subsequent calls are cached unless stale. */
  ensureSession(forceRefresh = false) {
    const now = Date.now();
    const fresh = now - this.lastChecked < this.ttlMs;
    if (!forceRefresh && this._status() !== 'unknown' && fresh) {
      return of(this._status() === 'authenticated');
    }

    const fetchPolicy = forceRefresh ? 'network-only' : 'cache-first';
    const seq = ++this.writeSeq;
    this.appliedWriteSeq = seq;
    let appliedResponse = false;

    return this.meGql
      .fetch({
        fetchPolicy,
        context: { skipGlobalErrorHandling: true, uiLoaderScope: 'authGuard' },
      })
      .pipe(
        map((res) => res.data?.me ?? null),
        tap((me) => {
          // A newer write or refresh start landed while this fetch was in
          // flight; applying this snapshot would roll it back.
          if (seq < this.appliedWriteSeq) return;
          this.appliedWriteSeq = seq;
          const newStatus = me ? 'authenticated' : 'unauthenticated';
          // Only a clear advances the session generation: a refresh that
          // keeps the same live session must leave it untouched so a genuine
          // 401 from an in-flight request stays authoritative instead of
          // being discarded as stale.
          if (
            newStatus === 'unauthenticated' &&
            this._status() !== 'unauthenticated'
          ) {
            this.sessionGenerationSeq += 1;
          }
          appliedResponse = true;
          this._user.set(me);
          this._status.set(newStatus);
          this.lastStateWriteSeq = seq;
          this.lastChecked = Date.now();
        }),
        map(() => appliedResponse && this._status() === 'authenticated'),
        catchError((error: unknown) => {
          this.lastChecked = Date.now();
          // Only an explicit session rejection clears client auth state; a
          // transient failure must not fake a logout. The auth guards never
          // reach this path while authenticated - they short-circuit on the
          // status signal first - and this query skips the global error
          // handling, so the rejection check happens here. The emitted `false`
          // always reports that this refresh did not confirm the session, so
          // callers keep their optimistic state instead of treating it as
          // verified server state.
          const { code, statusCode } = normalizeApolloError(error);
          const rejected = code === 'UNAUTHENTICATED' || statusCode === 401;
          if (rejected) {
            // An explicit server rejection is authoritative unless an actual
            // newer write (applied response, login, logout) has landed since
            // this fetch started. A newer refresh merely *starting* must not
            // discard it - otherwise an expired session could stay displayed
            // while the overlapping newer refresh only fails transiently.
            if (seq >= this.lastStateWriteSeq) {
              this.lastStateWriteSeq = seq;
              this.appliedWriteSeq = Math.max(this.appliedWriteSeq, seq);
              this._user.set(null);
              this._status.set('unauthenticated');
              this.sessionGenerationSeq += 1;
            }
            return of(false);
          }
          const keepSession = this._status() === 'authenticated';
          // Record this sequence even when the session is retained: the
          // operation still happened, so any older in-flight response stays
          // invalidated instead of applying a snapshot captured earlier
          // (which could resurrect a preference the newer operation changed).
          if (seq >= this.appliedWriteSeq) {
            this.appliedWriteSeq = seq;
            if (!keepSession) {
              this.lastStateWriteSeq = seq;
              this._user.set(null);
              this._status.set('unauthenticated');
              this.sessionGenerationSeq += 1;
            }
          }
          return of(false);
        }),
      );
  }

  /**
   * Records fresh user data for the current session. Deliberately does not
   * advance the session generation: callers also use this for ordinary
   * refreshes (profile/checkout updates), and a routine refresh must not
   * invalidate an in-flight request's stale-session check. A genuine session
   * replacement clears the user first, which does advance the generation.
   */
  markAuthenticated(user: CurrentUserQuery['me'] | null | undefined) {
    this.appliedWriteSeq = ++this.writeSeq;
    this.lastStateWriteSeq = this.appliedWriteSeq;
    this._user.set(user ?? null);
    this._status.set('authenticated');
    this.lastChecked = Date.now();
  }

  markUnauthenticated() {
    this.appliedWriteSeq = ++this.writeSeq;
    this.lastStateWriteSeq = this.appliedWriteSeq;
    this._user.set(null);
    this._status.set('unauthenticated');
    this.sessionGenerationSeq += 1;
    this.lastChecked = Date.now();
  }

  /**
   * Invalidate any previously displayed identity without claiming to know the
   * new session yet - e.g. a successful login/registration that switches to a
   * different account. In-flight refreshes started before this point can no
   * longer resurrect the old identity, and a transient failure loading the new
   * account leaves the state `unknown` instead of showing the previous user.
   */
  clearUser(): void {
    this.appliedWriteSeq = ++this.writeSeq;
    this.lastStateWriteSeq = this.appliedWriteSeq;
    this._user.set(null);
    this._status.set('unknown');
    this.sessionGenerationSeq += 1;
    this.lastChecked = Date.now();
  }

  /**
   * Monotonic token for the current session. It advances only when the
   * underlying session is replaced or cleared, so request handlers can
   * capture it at start and ignore stale logout decisions after a newer
   * login/logout without an ordinary refresh invalidating a genuine 401.
   */
  sessionGeneration(): number {
    return this.sessionGenerationSeq;
  }

  hasPermission(...permissions: string[]): boolean {
    const currentUser = this._user();
    const currentPermissions: string[] =
      currentUser?.role?.permissions?.map(
        (permission) => permission?.name ?? '',
      ) ?? [];

    return permissions.every((permission) =>
      currentPermissions.includes(permission),
    );
  }

  hasAnyPermission(...permissions: string[]): boolean {
    const currentUser = this._user();
    const currentPermissions: string[] =
      currentUser?.role?.permissions?.map(
        (permission) => permission?.name ?? '',
      ) ?? [];

    return permissions.some((permission) =>
      currentPermissions.includes(permission),
    );
  }

  hasRole(...roles: string[]): boolean {
    const currentUser = this._user();
    return roles.includes(currentUser?.role?.name ?? '');
  }
}
